import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import LanguageMenu from './LanguageMenu/LanguageMenu';
import CurrencyMenu from './CurrencyMenu/CurrencyMenu';

import css from './TopbarDesktop.module.css';
import IconCard from '../../../../components/SavedCardDetails/IconCard/IconCard';
import { STUDENT } from '../../../../util/types';
// import userProfileImage from '../../../../assets/profile-image.png';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

// Menu for guests: groups Sign Up and Log In into a dropdown
const AnonymousMenu = () => {
  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        {/* Simple user icon + label to match screenshot */}
        <span className={css.topbarLinkLabel}>
          <IconCard type={"user"} />
          <FormattedMessage id="TopbarDesktop.signup" />
        </span>
      </MenuLabel>
      <MenuContent className={css.profileMenuContentBox}>
        <MenuItem key="SignupPage">
          <NamedLink className={css.menuLink} name="SignupPage">
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.signup" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="LoginPage">
          <NamedLink className={css.menuLink} name="LoginPage">
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.login" />
          </NamedLink>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const findTutorLink = () => {
  return (
    <NamedLink name="SearchPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.findTutor" />
      </span>
    </NamedLink>
  );
};
const aboutLink = () => {
  return (
    <NamedLink name="CMSPage" params={{ pageId: 'about' }} className={css.topbarLink} >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.about" />
      </span>
    </NamedLink>

  );
};

const howItWorkLink = () => {
  return (
    <NamedLink name="CMSPage" params={{ pageId: 'how-it-works' }} className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.howItWork" />
      </span>
    </NamedLink>
  );
};
const contactLink = isArabic => {
  const pageId = isArabic ? 'ar-contact-us' : 'contact-us';
  return (
    <NamedLink name="CMSPage" params={{ pageId }} className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.contact" />
      </span>
    </NamedLink>
  );
};


const InboxLink = ({ notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink className={css.topbarLink} name="InboxPage" params={{ tab: inboxTab }}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const MyLessonsLink = () => {
  return (
    <NamedLink className={css.topbarLink} name="MyLessonsPage">
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.myLessons" />
      </span>
    </NamedLink>
  );
};

const MyProgressLink = () => {
  return (
    <NamedLink className={css.topbarLink} name="MyProgressPage">
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.myProgress" defaultMessage="My Progress" />
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink, isAdmin }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const { firstName, lastName, displayName } = currentUser?.attributes?.profile || {};
  const { email } = currentUser?.attributes || {};
  const fullName = firstName ? `${firstName} ${lastName}` : displayName;
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const isFamilyParent = currentUser?.attributes?.profile?.publicData?.isFamilyParent === true;
  const isFamilyChild = currentUser?.attributes?.profile?.publicData?.isFamilyChild === true;
  const isIndependentStudent = userType === STUDENT && !isFamilyParent && !isFamilyChild;
  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="ProfileSettingsCard">
          <NamedLink
            name="ProfileSettingsPage"
            className={css.userProfile}
            params={{ tab: 'basic-info' }}
          >
            <div className={css.profileLeft}>
              <div className={css.profileName}>{fullName}</div>
              <div className={css.profileEmail}>{email}</div>
            </div>
            <div className={css.profileRight}>
              {/* <img src={userProfileImage} /> */}
              <Avatar user={currentUser} disableProfileLink />
            </div>
          </NamedLink>
        </MenuItem>
        {!isFamilyChild && (
          <MenuItem key="WalletPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('WalletPage'))}
              name="WalletPage"
            >
              {/* <span className={css.menuItemBorder} /> */}
              <FormattedMessage id="TopbarDesktop.myWallet" />
            </NamedLink>
          </MenuItem>
        )}
        {userType === STUDENT ? (
          <MenuItem key="MyLessonsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('MyLessonsPage'))}
              name="MyLessonsPage"
            >
              {/* <span className={css.menuItemBorder} /> */}
              <FormattedMessage id="TopbarDesktop.myLessons" />
            </NamedLink>
          </MenuItem>
        ) : null}
        {isIndependentStudent ? (
          <MenuItem key="MyProgressPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('MyProgressPage'))}
              name="MyProgressPage"
            >
              <FormattedMessage id="TopbarDesktop.myProgress" defaultMessage="My Progress" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="InboxPage">
          <NamedLink
            name="InboxPage"
            params={{ tab: userType == STUDENT ? 'orders' : "sales" }}
            className={classNames(css.menuLink, currentPageClass('InboxPage'))}
          >
            {/* <span className={css.menuItemBorder} /> */}
            <FormattedMessage id="TopbarDesktop.inbox" />
          </NamedLink>
        </MenuItem>
        {!isFamilyChild && (
          <MenuItem key="WalletPageReferFriend">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('WalletPage'))}
              name="WalletPage"
              to={{ hash: '#wallet-referral-section' }}
            >
              {/* <span className={css.menuItemBorder} /> */}
              <FormattedMessage id="TopbarDesktop.referFirend" />
            </NamedLink>
          </MenuItem>
        )}

        {isAdmin ? <MenuItem key="AdminPanel">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AdminPanel'))}
            name="AdminPanel"
          >
            {/* <span className={css.menuItemBorder} /> */}
            <FormattedMessage id="TopbarDesktop.adminPanelLink" />
          </NamedLink>
        </MenuItem> : null}
        {isAdmin ?
          <MenuItem key="NewBlogsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('NewBlogsPage'))}
              name="NewBlogsPage"
            >
              {/* <span className={css.menuItemBorder} /> */}
              <FormattedMessage id="TopbarDesktop.blogPanelLink" defaultMessage='Create blog' />
            </NamedLink>
          </MenuItem> : null}

        {isAdmin ? <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            {/* <span className={css.menuItemBorder} /> */}
            <FormattedMessage id="TopbarDesktop.myBlogsLink" defaultMessage='My Blogs' />
          </NamedLink>
        </MenuItem> : null}

        <MenuItem key="ProfileSettingsLink">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
            params={{ tab: 'basic-info' }}
          >
            {/* <span className={css.menuItemBorder} /> */}
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            {/* <span className={css.menuItemBorder} /> */}
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            {/* <span className={css.menuItemBorder} /> */}
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
  } = props;
  const [mounted, setMounted] = useState(false);
  const isAdmin = currentUser?.attributes?.profile?.protectedData?.isAdmin;
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const isStudent = userType === STUDENT;
  const isFamilyParent = currentUser?.attributes?.profile?.publicData?.isFamilyParent === true;
  const isFamilyChild = currentUser?.attributes?.profile?.publicData?.isFamilyChild === true;
  const isIndependentStudent = isStudent && !isFamilyParent && !isFamilyChild;
  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      isAdmin={isAdmin}
    />
  ) : null;
  const anonymousMenuMaybe = isAuthenticatedOrJustHydrated ? null : <AnonymousMenu />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  const isArabic = (intl?.locale || '').toLowerCase().startsWith('ar');
  const showCreateListingCta = !isAuthenticated && showCreateListingsLink;

  return (
    <nav className={classes} suppressHydrationWarning>
      <div className={css.headerWrapper}>
        <div className={css.headerLeftSide}>
          <LinkedLogo
            className={css.logoLink}
            layout="desktop"
            alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
            linkToExternalSite={isArabic ? undefined : config?.topbar?.logoLink}
          />
          <div className={css.leftMenuLinks}>
            {howItWorkLink()}
            {aboutLink()}
            {contactLink(isArabic)}

            {authenticatedOnClientSide && isStudent ? <MyLessonsLink /> : null}
            {authenticatedOnClientSide && isIndependentStudent ? <MyProgressLink /> : null}
            {inboxLinkMaybe}
          </div>
        </div>
        {/* {searchFormMaybe} */}

        <div className={css.headerRightSide}>
          {/* {findTutorLink()} */}
          {profileMenuMaybe}
          {/* {anonymousMenuMaybe} */}
          {isAuthenticated ? null : (
            <NamedLink className={css.topbarLink} name="SignupPage">
              <FormattedMessage id="TopbarDesktop.signup" />
            </NamedLink>
          )}
          {isAuthenticated ? null : (
            <NamedLink className={css.topbarLink} name="LoginPage">
              <FormattedMessage id="TopbarDesktop.login" />
            </NamedLink>
          )}
          <CurrencyMenu currentPage={currentPage} />
          <LanguageMenu currentPage={currentPage} />
          <div className={css.customLinksMenu}>
            <CustomLinksMenu
              currentPage={currentPage}
              customLinks={customLinks}
              intl={intl}
              hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
              showCreateListingsLink={showCreateListingCta}
            />
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopbarDesktop;
